// ANGULAR
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
// SERVICES
import { GlobalService } from '@services/global.service';
// MODELS
import { Secret } from '@models/secretos.model';
import { PostPutAppConfigurationRequest, Application, IPsLog, ApplicationRotatePasswordInfo } from '@models/aplicaciones.model';
// RXJS
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AplicacionesService {
  readonly httpOptions: { headers: HttpHeaders, params: HttpParams } = { headers: new HttpHeaders({ 'content-type': 'application/json', 'accept': 'application/json' }), params: new HttpParams() };

  constructor(
    private globalService: GlobalService,
    private http: HttpClient
  ) { }

  getAplicaciones(ids: string[], fullData: boolean): Observable<Application[]> {
    let params: HttpParams = new HttpParams();
    if (fullData !== null && fullData !== undefined) { params = params.set('fullData', fullData); }
    if (ids && ids.length > 0) {
      const formattedIds: string = ids.join('|');
      params = params.set('ids', formattedIds);
    }

    return this.http.get<Application[]>(`${this.globalService.apiUrl.url}/Aplicaciones`, { ...this.httpOptions, params });
  }

  postCreateAplicacion(application: Application): Observable<void> {
    return this.http.post<void>(`${this.globalService.apiUrl.url}/Aplicaciones`, application, this.httpOptions);
  }

  getAplicacion(applicationId: string, fullData: boolean): Observable<Application> {
    let params: HttpParams = new HttpParams();
    if (fullData !== null && fullData !== undefined) { params = params.set('fullData', fullData); }

    return this.http.get<Application>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}`, { ...this.httpOptions, params });
  }

  putEditAplicacion(applicationId: string, application: Application): Observable<void> {
    return this.http.put<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}`, application, this.httpOptions);
  }

  deleteAplicacion(applicationId: string): Observable<void> {
    return this.http.delete<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}`, this.httpOptions);
  }

  getIPs(applicationId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/IPs`, this.httpOptions);
  }

  getIPsLog(applicationId: string): Observable<IPsLog> {
    return this.http.get<IPsLog>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/IPs/Log`, this.httpOptions);
  }

  deleteIPsLog(applicationId: string): Observable<void> {
    return this.http.delete<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/IPs/Log`, this.httpOptions);
  }

  getIPsKeys(applicationId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/IPsKeys`, this.httpOptions);
  }

  putEditIPsKeys(applicationId: string, ipsKeys: string[]): Observable<void> {
    return this.http.put<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/IPsKeys`, ipsKeys, this.httpOptions);
  }

  getIPsKeysAll(applicationId: string, expand: boolean): Observable<Record<string, string[]>> {
    let params: HttpParams = new HttpParams();
    params = params.set('expandir', expand);

    return this.http.get<Record<string, string[]>>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/IPsKeysAll`, { ...this.httpOptions, params });
  }

  postAddIPKeyToApplication(applicationId: string, key: string): Observable<void> {
    return this.http.post<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/IPsKeys/${key}`, null, this.httpOptions);
  }

  deleteIPKeyToApplication(applicationId: string, key: string): Observable<void> {
    return this.http.delete<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/IPsKeys/${key}`, this.httpOptions);
  }

  postAddSecretoToApplication(applicationId: string, secret: Secret): Observable<void> {
    let params: HttpParams = new HttpParams();
    params = params.set('valor', secret.valor);

    return this.http.post<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/Secretos/${secret.nombre}`, null, { ...this.httpOptions, params });
  }

  putSecretoToApplication(applicationId: string, secret: Secret): Observable<void> {
    let params: HttpParams = new HttpParams();
    params = params.set('valor', secret.valor);

    return this.http.put<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/Secretos/${secret.nombre}`, null, { ...this.httpOptions, params });
  }

  deleteSecretoToApplication(applicationId: string, key: string): Observable<void> {
    return this.http.delete<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/Secretos/${key}`, this.httpOptions);
  }

  postRotatePasswordToApplication(applicationId: string, newPassword: string): Observable<ApplicationRotatePasswordInfo> {
    let params: HttpParams = new HttpParams();
    params = params.set('newPassword', newPassword);

    return this.http.post<ApplicationRotatePasswordInfo>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/RotatePassword`, null, { ...this.httpOptions, params });
  }

  postRotateOldPasswordToApplication(applicationId: string): Observable<void> {
    return this.http.post<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/RevokeOldPassword`, null, this.httpOptions);
  }

  getAppsFromIpsKey(key: string): Observable<string[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('key', key);

    return this.http.get<string[]>(`${this.globalService.apiUrl.url}/Aplicaciones/GetAppsFromIpsKey`, { ...this.httpOptions, params });
  }

  getAppsFromIP(ip: string): Observable<string[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('ip', ip);

    return this.http.get<string[]>(`${this.globalService.apiUrl.url}/Aplicaciones/GetAppsFromIP`, { ...this.httpOptions, params });
  }

  postAddConfiguracionToApplication(applicationId: string, postPutAppConfigurationRequest: PostPutAppConfigurationRequest): Observable<void> {
    return this.http.post<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/Configuracion`, postPutAppConfigurationRequest, this.httpOptions);
  }

  putEditConfiguracionToApplication(applicationId: string, postPutAppConfigurationRequest: PostPutAppConfigurationRequest): Observable<void> {
    return this.http.put<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/Configuracion`, postPutAppConfigurationRequest, this.httpOptions);
  }

  deleteConfiguracionToApplication(applicationId: string, key: string): Observable<void> {
    return this.http.delete<void>(`${this.globalService.apiUrl.url}/Aplicaciones/${applicationId}/Configuracion/${key}`, this.httpOptions);
  }
}